import axios from "axios"

// COSTANTI
const TEMPERATURE_KELVIN_0C = 273.15
const VELOCITY_MS_TO_KMH = 3.6
const ENERGY_WH_TO_MJ = 3600 * 1e-6


// PREVISIONI METEO
const API_KEY_OPENWEATHER = "b40a40f5996145796dfac1ff4800c7b3"

export function getWeatherForecast(lat, lon, cnt) {
    return axios.get("https://api.openweathermap.org/data/2.5/forecast", {
        params: {
            lat,
            lon,
            cnt,
            appid: API_KEY_OPENWEATHER,
            units: "metric",
            lang: 'it'
        }
    })
}


// BULBO UMIDO
const MAGNUS_PARAM_A = 17.62
const MAGNUS_PARAM_B = 243.12

const STULL_PARAM_1 = 0.151_977
const STULL_PARAM_2 = 8.313_659
const STULL_PARAM_3 = 1.676_331
const STULL_PARAM_4 = 0.003_918_38
const STULL_PARAM_5 = 0.023_101
const STULL_PARAM_6 = 4.686_035

/**
 * Calcola il punto di rugiada secondo la formula di Magnus-Tetens
 * @param t temperatura atmosferica in °C
 * @param h umidità relativa in %
 * @return punto di rugiada in °C
 */
export function calculateDewPoint(t, h) {
    let alpha = Math.log(h / 100) + MAGNUS_PARAM_A * t / (MAGNUS_PARAM_B + t);
    let dp = (MAGNUS_PARAM_B * alpha) / (MAGNUS_PARAM_A - alpha);
    return Number(dp.toFixed(2))
}

/**
 * Calcola il punto di rugiada secondo la formula semplificata
 * @param t temperatura atmosferica in °C
 * @param h umidità relativa in %
 * @return punto di rugiada in °C
 */
export function calculateDewPointSimple(t, h) {
    let dp = t - (100 - h) / 5;
    return Number(dp.toFixed(2))
}

/**
 * Calcola la temperatura di bulbo umido secondo la formula di Stull(2011)
 * @param t temperatura atmosferica in °C
 * @param h umidità relativa in %
 * @return temperatura di bulbo umido
 */
export function calculateWetBulbTemperature(t, h) {
    let wb = t * Math.atan(STULL_PARAM_1 * Math.sqrt(h + STULL_PARAM_2)) + Math.atan(t + h) - Math.atan(h - STULL_PARAM_3)
        + STULL_PARAM_4 * h * Math.sqrt(h) * Math.atan(STULL_PARAM_5 * h) - STULL_PARAM_6;
    return Number(wb.toFixed(2))
}

/**
 * Calcola la temperatura di bulbo umido secondo la regola di 1/3
 * @param t temperatura atmosferica in °C
 * @param dp punto di rugiada in °C
 * @return temperatura di bulbo umido
 */
export function calculateWetBulbTemperatureSimple(t, dp) {
    let wb = t - (t - dp) / 3;
    return Number(wb.toFixed(2))
}


// VPD
const VPD_PARAM_1 = 0.6108
const VPD_PARAM_2 = 17.27
const VPD_PARAM_3 = 237.3

/**
 * Calcola il deficit di pressione di vapore
 * @param t temperatura atmosferica in °C
 * @param h umidità relativa in %
 * @return vapor pressure deficit (VPD) in kPa
 */
export function calculateVaporPressureDeficit(t, h) {
    // pressione di vapore saturo
    let es = VPD_PARAM_1 * Math.exp(VPD_PARAM_2 * t / (VPD_PARAM_3 + t))
    // pressione di vapore reale
    let ea = h * es / 100
    return Number((es - ea).toFixed(2))
}

/**
 * Calcola la pendenza della curva della pressione di vapore saturo in funzione della temperatura
 * @param {Number} t temperatura atmosferica in °C
 * @return {Number} delta in kPa/°C
 */
export function calculateVaporPressureTemperatureDelta(t) {
    // pressione di vapore saturo
    let es = VPD_PARAM_1 * Math.exp(VPD_PARAM_2 * t / (VPD_PARAM_3 + t))
    // pendenza della curva (nota: 4098=237.3*17.27)
    let delta = VPD_PARAM_2 * VPD_PARAM_3 * es / Math.pow(VPD_PARAM_3 + t, 2)
    return Number(delta.toPrecision(3))
}


// EVAPOTRASPIRAZIONE
const ET_PARAM_LAMBDA = 2.45    // calore latente di vaporizzazione (MJ/kg)
const ET_PARAM_GAMMA = 0.067    // costante psicrometrica (kPa/°C)
const ET_PARAM_RHO = 1.293 		// kg m−3	densità dell’aria (a T=25 °C)
const ET_PARAM_CP = 1.013 		// kJ kg-1 °C-1	calore specifico dell’aria umida a pressione costante
const ET_PARAM_RC = 60 			// s m-1 resistenza colturale del prato di riferimento
const ET_PARAM_1 = 0.6          // frazione radiazione disponibile
const ET_PARAM_2 = 0.67         // coefficiente altezza del piano di spostamento nullo
const ET_PARAM_3 = 0.1          // coefficiente rugosità della superficie evaporante
const ET_PARAM_4 = 0.41         // coefficiente di von Karman
const ET_PARAM_Z = 2.5          // altezza a cui è misurata la velocità del vento

/**
 * Calcola la evapotraspirazione della coltura
 * @param {Number} t temperatura atmosferica in °C
 * @param {Number} u umidità atmosferica relativa in %
 * @param {Number} r radiazione solare totale giornaliera in Wh/m2
 * @param {Number} v velocità del vento in km/h
 * @param {Number} h altezza della coltura
 * @return {Number} evapotraspirazione della coltura in mm/day
 */
export function calculateEvapotranspiration(t, u, r, v, h) {
    let vpd = calculateVaporPressureDeficit(t, u)
    let delta = calculateVaporPressureTemperatureDelta(t)
    // velocità del vento in m/s
    let w = v / VELOCITY_MS_TO_KMH
    // radiazione disponibile (conversione da wattora/m2 in megajoule/m2)
    let a = ET_PARAM_1 * r * ENERGY_WH_TO_MJ
    // altezza del piano di spostamento nullo
    let d = ET_PARAM_2 * h
    // rugosità della superficie evaporante
    let z0 = ET_PARAM_3 * h
    // resistenza aereodinamica (s m-1)
    let ra = Math.log((ET_PARAM_Z-d)/z0) * Math.log((ET_PARAM_Z-d)/(h-d)) / (Math.pow(ET_PARAM_4, 2) * w)
    // evapotraspirazione (Penman-Monteith)
    let et = 1 / ET_PARAM_LAMBDA * (delta * a + ET_PARAM_RHO * ET_PARAM_CP * vpd / ra) / (delta + ET_PARAM_GAMMA * (1 + ET_PARAM_RC / ra))
    return Number(et.toPrecision(3))
}
